// Core
import { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
import {
  Button,
  FieldSelect,
  FieldText,
  SectionCard,
  ValidityDateRangePicker,
} from "components/AtomsV2";
import BusinessValidityChangeNotice from "components/BusinessValidityChangeNotice";
import { Icon } from "components/ShellV2";

// Utils
import { MARKET_RATES } from "utils/constants";
import { defaultBusinessEndDate, lisbonToday } from "utils/businessValidity";

const MIN_COMP = 0.0;
const MAX_COMP = 1.0;
const MIN_DIVISOR = 0.01;
const MAX_DIVISOR = 1.0;

function Market({ data, isLoading, onUpdate, readOnly }) {
  const initialStartDate = data?.start_date || lisbonToday();
  const initialEndDate = data?.end_date || defaultBusinessEndDate(initialStartDate);
  const [original, setOriginal] = useState({
    ...data,
    start_date: initialStartDate,
    end_date: initialEndDate,
  });
  const [marketRate, setMarketRate] = useState(data.market_rate);
  const [divisor, setDivisor] = useState(data.market_divisor);
  const [compensation, setCompensation] = useState(data.market_compensation);
  const [smallIncrement, setSmallIncrement] = useState(data.market_small_increment);
  const [fixedPrice, setFixedPrice] = useState(data.market_fixed_price);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  const { t } = useTranslation(["community", "common"]);

  const canSave =
    !isLoading &&
    (original.market_rate !== marketRate ||
      original.market_divisor !== divisor ||
      original.market_compensation !== compensation ||
      original.market_small_increment !== smallIncrement ||
      original.market_fixed_price !== fixedPrice ||
      original.start_date !== startDate ||
      original.end_date !== endDate) &&
    startDate &&
    (!endDate || endDate >= startDate);

  const onUpdateClicked = async () => {
    if (canSave) {
      const newData = {
        market_rate: marketRate,
        market_divisor: divisor,
        market_compensation: compensation,
        market_small_increment: smallIncrement,
        market_fixed_price: fixedPrice,
        start_date: startDate,
        end_date: endDate || null,
      };
      const success = await onUpdate(newData, t("market.updated_ok"));

      if (success) {
        setOriginal({ ...newData, end_date: endDate });
      }
    }
  };

  const onDivisorChanged = (v) => {
    const value = Number(v);
    if (value <= 0) {
      setDivisor(MIN_DIVISOR);
    } else if (value > MAX_DIVISOR) {
      setDivisor(MAX_DIVISOR);
    } else {
      setDivisor(value);
    }
  };

  const onCompensationChanged = (v) => {
    const value = Number(v);
    if (value < MIN_COMP) {
      setCompensation(MIN_COMP);
    } else if (value > MAX_COMP) {
      setCompensation(MAX_COMP);
    } else {
      setCompensation(value);
    }
  };

  return (
    <SectionCard
      id="market"
      icon="storefront"
      title={t("market.title")}
      action={
        readOnly ? null : (
          <Button variant="ghost" size="sm" disabled={!canSave} onClick={onUpdateClicked}>
            <Icon name="save" />
            {t("market.update")}
          </Button>
        )
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {!readOnly && (
          <ValidityDateRangePicker
            className="sm:col-span-2"
            value={{ start: startDate, end: endDate }}
            onChange={({ start, end }) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
        )}
        <FieldSelect
          label={t("market.market_rate")}
          value={marketRate}
          options={MARKET_RATES}
          onChange={setMarketRate}
          disabled={readOnly}
        />
        {marketRate === "VANILLA_IMR" && (
          <FieldText
            label={t("market.divisor")}
            type="number"
            min={MIN_DIVISOR}
            max={MAX_DIVISOR}
            value={divisor}
            onChange={onDivisorChanged}
            disabled={readOnly}
          />
        )}
        {marketRate === "VANILLA_SDR" && (
          <FieldText
            label={t("market.compensation")}
            type="number"
            min={MIN_COMP}
            max={MAX_COMP}
            value={compensation}
            onChange={onCompensationChanged}
            disabled={readOnly}
          />
        )}
        {marketRate === "VANILLA_CV" && (
          <FieldText
            label={t("market.small_increment")}
            type="number"
            min={MIN_COMP}
            max={MAX_COMP}
            value={smallIncrement}
            onChange={(v) => setSmallIncrement(Number(v))}
            disabled={readOnly}
          />
        )}
        {marketRate === "FIXED_PRICE" && (
          <FieldText
            label={t("market.fixed_price")}
            type="number"
            value={fixedPrice}
            onChange={(v) => setFixedPrice(Number(v))}
            hint={t("market.fixed_price_hint")}
            disabled={readOnly}
          />
        )}
      </div>
      {!readOnly && (
        <BusinessValidityChangeNotice
          originalStartDate={original.start_date}
          originalEndDate={original.end_date}
          startDate={startDate}
          endDate={endDate}
          className="mt-4"
        />
      )}
    </SectionCard>
  );
}

Market.defaultProps = {
  isLoading: false,
  readOnly: false,
};

Market.propTypes = {
  data: PropTypes.shape({
    market_rate: PropTypes.string,
    market_divisor: PropTypes.number,
    market_compensation: PropTypes.number,
    market_small_increment: PropTypes.number,
    market_fixed_price: PropTypes.number,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
  }).isRequired,
  isLoading: PropTypes.bool,
  readOnly: PropTypes.bool,
  onUpdate: PropTypes.func.isRequired,
};

export default Market;
